import { useMemo, useState, type ReactNode } from 'react';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery } from '@shared/supabase/hooks';
import { Skeleton, VerifiedBadge } from '@shared/ui';
import {
  IconCreatorNode, IconBrandNode, IconCoPublish, IconReshare, IconTag, IconMention, NamedIcon,
} from '@shared/ui/icons';
import { useHandleVerification } from '@shared/hooks/useHandleVerification';

/** One row from public.get_creator_network. */
interface NetRow {
  partner_handle: string;
  partner_type: 'brand' | 'creator' | 'unknown';
  partner_brand_id: string | null;
  partner_brand_name: string | null;
  partner_provider_id: string | null;
  direction: 'out' | 'in' | 'mutual';
  coauthor_ct: number;
  reshare_ct: number;
  tag_ct: number;
  mention_ct: number;
  total_ct: number;
  followers: number | null;
  last_seen: string | null;
}

export interface OpenPartner {
  handle: string;
  providerId: string;
  followers: number | null;
}

const RCOL = { copublish: '#4a9eff', reshare: '#6b7280', tag: '#50c878', mention: '#9B5DE5' };
const RLBL = { copublish: 'co-publish', reshare: 'reshare', tag: 'tag', mention: 'mention' };
const DCOL: Record<string, string> = { mutual: '#ffd87a', in: '#50c878', out: '#e0556b' };
const ARR: Record<string, string> = { out: 'arrow_right', in: 'arrow_left', mutual: 'swap' };
const fmtF = (n: number | null) =>
  n == null ? '—' : n >= 1e6 ? (n / 1e6).toFixed(1) + 'M' : n >= 1e3 ? Math.round(n / 1e3) + 'K' : '' + n;
const strength = (r: NetRow) => r.coauthor_ct * 100 + r.reshare_ct * 10 + r.tag_ct * 3 + r.mention_ct;
const partnerLabel = (r: NetRow) => (r.partner_type === 'brand' && r.partner_brand_name ? r.partner_brand_name : r.partner_handle);
const openable = (r: NetRow, cb?: (p: OpenPartner) => void) => {
  if (r.partner_provider_id && cb) cb({ handle: r.partner_handle, providerId: r.partner_provider_id, followers: r.followers });
  else window.open(`https://instagram.com/${r.partner_handle}/`, '_blank', 'noopener');
};

function Pill({ value, onChange, labels }: { value: boolean; onChange: (v: boolean) => void; labels: [string, string] }) {
  return (
    <div style={{ display: 'inline-flex', background: '#14171c', border: '1px solid #2a2d35', borderRadius: 999, padding: 3, gap: 2 }}>
      {[false, true].map((v) => (
        <button key={String(v)} type="button" onClick={() => onChange(v)}
          style={{ border: 'none', background: value === v ? '#2563eb' : 'transparent', color: value === v ? '#fff' : 'var(--muted)', font: 'inherit', fontSize: 'var(--fs-small)', padding: '5px 14px', borderRadius: 999, cursor: 'pointer' }}>
          {v ? labels[1] : labels[0]}
        </button>
      ))}
    </div>
  );
}

const TYPE_ICON = (tt: string, size: number, color: string) =>
  tt === 'brand' ? <IconBrandNode size={size} color={color} /> : <IconCreatorNode size={size} color={color} />;
const EDGE_ICON: Record<string, (s: number) => JSX.Element> = {
  copublish: (s) => <IconCoPublish size={s} />, reshare: (s) => <IconReshare size={s} />,
  tag: (s) => <IconTag size={s} />, mention: (s) => <IconMention size={s} />,
};

/** Shared floating tooltip. */
function useTip() {
  const [tip, setTip] = useState<{ x: number; y: number; node: ReactNode } | null>(null);
  const el = tip ? (
    <div style={{ position: 'fixed', left: Math.min(tip.x + 14, window.innerWidth - 250), top: tip.y + 14, zIndex: 60, background: '#14171c', border: '1px solid #2a2d35', borderRadius: 9, padding: '9px 11px', fontSize: 'var(--fs-small)', pointerEvents: 'none', boxShadow: '0 6px 22px rgba(0,0,0,.55)', maxWidth: 240 }}>
      {tip.node}
    </div>
  ) : null;
  return { setTip, tipEl: el };
}

function Legend({ items }: { items: Array<[string, string]> }) {
  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 'var(--fs-small)', color: 'var(--muted)', alignItems: 'center' }}>
      {items.map(([color, label]) => (
        <span key={label}><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: color, marginRight: 5, verticalAlign: 'middle' }} />{label}</span>
      ))}
    </div>
  );
}

// ───────────────────────── Brands table (Option A) ─────────────────────────
function BrandsTable({ rows, onOpen }: { rows: NetRow[]; onOpen?: (p: OpenPartner) => void }) {
  const [onlyBrands, setOnlyBrands] = useState(true);
  const { setTip, tipEl } = useTip();
  const verif = useHandleVerification(rows.map((r) => r.partner_handle));
  const list = useMemo(
    () => rows.filter((r) => (r.partner_type === 'brand' || r.partner_type === 'unknown') && (!onlyBrands || r.partner_type === 'brand')).sort((a, b) => strength(b) - strength(a)),
    [rows, onlyBrands],
  );
  const maxTot = Math.max(1, ...list.map((r) => r.total_ct));
  if (!rows.some((r) => r.partner_type === 'brand' || r.partner_type === 'unknown')) return null;
  return (
    <div style={{ marginBottom: 20 }}>
      {tipEl}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
        <Pill value={onlyBrands} onChange={setOnlyBrands} labels={['All', 'Brands only']} />
        <Legend items={[[RCOL.copublish, 'co-publish'], [RCOL.reshare, 'reshare'], [RCOL.tag, 'tag'], [RCOL.mention, 'mention']]} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {list.map((r) => {
          const segs: Array<[keyof typeof RCOL, number]> = [['copublish', r.coauthor_ct], ['reshare', r.reshare_ct], ['tag', r.tag_ct], ['mention', r.mention_ct]];
          return (
            <div key={r.partner_handle} onClick={() => openable(r, onOpen)}
              style={{ display: 'grid', gridTemplateColumns: '18px minmax(120px,1.6fr) 72px 1fr 40px', gap: 10, alignItems: 'center', padding: '7px 6px', borderBottom: '1px solid #20242c', cursor: 'pointer' }}>
              <span style={{ color: 'var(--muted)', fontSize: 'var(--fs-small)', display: 'inline-flex', alignItems: 'center' }}><NamedIcon name={ARR[r.direction]} size={13} color="#9aa" /></span>
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.partner_type === 'brand' && r.partner_brand_name
                  ? <><b>{r.partner_brand_name}</b> <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--dim)' }}>@{r.partner_handle}</span></>
                  : <b>@{r.partner_handle}</b>}
                {' '}<VerifiedBadge {...verif(r.partner_handle)} size={14} />
              </span>
              <span style={{ fontSize: 'var(--fs-micro)', padding: '1px 7px', borderRadius: 6, border: `1px solid ${r.partner_type === 'brand' ? '#4a9eff55' : 'rgba(150,150,160,.3)'}`, color: r.partner_type === 'brand' ? 'var(--accent-text)' : '#bcc', display: 'inline-flex', gap: 4, alignItems: 'center', width: 'fit-content' }}>
                {TYPE_ICON(r.partner_type, 12, r.partner_type === 'brand' ? '#4a9eff' : '#9aa0aa')} {r.partner_type}
              </span>
              <div style={{ display: 'flex', height: 13, width: `${Math.max(10, (r.total_ct / maxTot) * 100)}%`, borderRadius: 6, overflow: 'hidden', gap: 1, background: '#20242c' }}>
                {segs.map(([k, c]) => c ? (
                  <div key={k} style={{ flex: c, background: RCOL[k] }}
                    onMouseMove={(e) => setTip({ x: e.clientX, y: e.clientY, node: <span>{EDGE_ICON[k](13)} {RLBL[k]} · <b>{c}</b></span> })}
                    onMouseLeave={() => setTip(null)} />
                ) : null)}
              </div>
              <b style={{ textAlign: 'right' }}>{r.total_ct}</b>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ───────────────────────── Network graph (Option C) ─────────────────────────
const CAP = 30;
function NetworkGraph({ rows, handle, followers, onOpen }: { rows: NetRow[]; handle: string; followers: number | null; onOpen?: (p: OpenPartner) => void }) {
  const [onlyBrands, setOnlyBrands] = useState(false);
  const { setTip, tipEl } = useTip();
  const all = useMemo(
    () => rows.filter((r) => (r.partner_type === 'creator' || r.partner_type === 'brand') && (!onlyBrands || r.partner_type === 'brand')).sort((a, b) => strength(b) - strength(a)),
    [rows, onlyBrands],
  );
  if (!all.length) return null;
  const nodes = all.slice(0, CAP);
  const hidden = all.length - nodes.length;
  const W = 900, H = 680, cx = W / 2, cy = H / 2, Rin = 120, Rout = 305;
  const tots = nodes.map((n) => n.total_ct), maxTot = Math.max(...tots), minTot = Math.min(...tots);
  const sq = (v: number | null) => Math.sqrt(v || 1);
  const sqs = nodes.map((n) => sq(n.followers)), maxS = Math.max(...sqs), minS = Math.min(...sqs);
  const laid = nodes.map((n, i) => {
    const ang = (i / nodes.length) * 2 * Math.PI - Math.PI / 2;
    const t = (n.total_ct - minTot) / ((maxTot - minTot) || 1);
    const dist = Rout - (Rout - Rin) * t;
    const r = 13 + 28 * ((sq(n.followers) - minS) / ((maxS - minS) || 1));
    return { n, x: cx + dist * Math.cos(ang), y: cy + dist * Math.sin(ang), r };
  });
  return (
    <div>
      {tipEl}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 8 }}>
        <Pill value={onlyBrands} onChange={setOnlyBrands} labels={['All', 'Brands only']} />
        <Legend items={[[DCOL.mutual, 'mutual'], [DCOL.in, 'incoming'], [DCOL.out, 'outgoing']]} />
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', maxHeight: 560 }}>
        {laid.map(({ n, x, y }) => (
          <line key={'l' + n.partner_handle} x1={cx} y1={cy} x2={x} y2={y} stroke={DCOL[n.direction]} strokeWidth={1.8} strokeOpacity={0.5} />
        ))}
        {laid.map(({ n, x, y, r }) => {
          const s = r * 1.15;
          return (
            <g key={n.partner_handle} style={{ cursor: 'pointer' }}
              onMouseMove={(e) => setTip({ x: e.clientX, y: e.clientY, node: <NodeTip n={n} /> })}
              onMouseLeave={() => setTip(null)} onClick={() => openable(n, onOpen)}>
              <circle cx={x} cy={y} r={r} fill="#181b21" stroke="#3a3f4a" strokeWidth={1.5} />
              <g transform={`translate(${x - s / 2},${y - s / 2}) scale(${s / 24})`}>
                {TYPE_ICON(n.partner_type, 24, '#dfe3ea')}
              </g>
              <text x={x} y={y + r + 13} textAnchor="middle" fill="#cdd2da" fontSize={11}>{partnerLabel(n).slice(0, 16)}</text>
              <text x={x} y={y + r + 25} textAnchor="middle" fill="#6b7280" fontSize={10}>{fmtF(n.followers)}</text>
            </g>
          );
        })}
        <circle cx={cx} cy={cy} r={42} fill="#1a1d23" stroke="#e1306c" strokeWidth={2.5} />
        <g transform={`translate(${cx - 13},${cy - 18}) scale(1.1)`}><IconCreatorNode size={24} color="#fff" /></g>
        <text x={cx} y={cy + 25} textAnchor="middle" fill="#fff" fontSize={11} fontWeight={700}>@{handle}</text>
        <text x={cx} y={cy + 58} textAnchor="middle" fill="#bcd0ff" fontSize={11} fontWeight={600}>{fmtF(followers)} followers</text>
      </svg>
      {hidden > 0 && <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--dim)', textAlign: 'center', marginTop: 4 }}>+{hidden} more (showing top {CAP} by interaction)</div>}
    </div>
  );
}

function NodeTip({ n }: { n: NetRow }) {
  const rows: Array<[keyof typeof RLBL, number]> = [['copublish', n.coauthor_ct], ['reshare', n.reshare_ct], ['tag', n.tag_ct], ['mention', n.mention_ct]];
  return (
    <div>
      <div style={{ fontWeight: 'var(--fw-bold)' }}>{partnerLabel(n)}</div>
      <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--muted)', marginTop: 2 }}>{n.direction} · {fmtF(n.followers)} followers</div>
      {rows.filter(([, c]) => c).map(([k, c]) => (
        <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, color: 'var(--text-2)' }}>{EDGE_ICON[k](14)} {RLBL[k]} · <b>{c}</b></div>
      ))}
    </div>
  );
}

// ───────────────────────── Section ─────────────────────────
export default function CollabSection({ providerId, handle, followers, onOpenCreator }: { providerId: string; handle: string; followers: number | null; onOpenCreator?: (p: OpenPartner) => void }) {
  const q = useSupabaseQuery<NetRow[]>(
    ['creator-network', providerId],
    async () => supabase.rpc('get_creator_network', { p_provider_id: providerId }),
  );
  if (q.isLoading) return <Skeleton height={320} />;
  const rows = q.data ?? [];
  if (!rows.length) return <div style={{ fontSize: 'var(--fs-body)', color: 'var(--dim)' }}>No collaborations detected yet.</div>;
  return (
    <>
      <BrandsTable rows={rows} onOpen={onOpenCreator} />
      <NetworkGraph rows={rows} handle={handle} followers={followers} onOpen={onOpenCreator} />
    </>
  );
}
